import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import type { AgentActionReceiptWireV1, AgentActionEffectWireV1 } from '@xavior/contracts'
import {
  createAgentEffectsStore,
  agentResourceKey,
  type AgentEffectsStore,
  type ActiveAgentEffect,
} from '@/lib/agent-effects/agentEffectsStore'
import { getSelectedWorkspaceId } from '@/lib/api/workspace-context'
import { occInstanceToken } from '@/lib/agent-effects/occToken'

/**
 * React layer over the pure `agentEffectsStore`. One store per session,
 * scoped to the active workspace (receipts for another workspace are dropped by
 * the store; a workspace switch triggers `reset()`). Effects expire on a single
 * shared interval tick. The store carries TRANSIENT animation state only — it is
 * never the source of truth for domain data (that stays in TanStack Query).
 */

const EFFECT_TICK_MS = 500

const AgentEffectsCtx = createContext<AgentEffectsStore | null>(null)

export function AgentEffectsProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<AgentEffectsStore | null>(null)
  if (!storeRef.current) {
    storeRef.current = createAgentEffectsStore({ workspaceId: () => getSelectedWorkspaceId() })
  }
  const store = storeRef.current

  useEffect(() => {
    let lastWs = getSelectedWorkspaceId()
    const id = window.setInterval(() => {
      const ws = getSelectedWorkspaceId()
      if (ws !== lastWs) {
        lastWs = ws
        store.reset() // workspace switch — clear stale effects
      }
      store.tickExpiry()
    }, EFFECT_TICK_MS)
    return () => window.clearInterval(id)
  }, [store])

  return <AgentEffectsCtx.Provider value={store}>{children}</AgentEffectsCtx.Provider>
}

export function useAgentEffectsStore(): AgentEffectsStore {
  const store = useContext(AgentEffectsCtx)
  if (!store) throw new Error('useAgentEffectsStore must be used within an AgentEffectsProvider')
  return store
}

/**
 * Subscribe to the agent effect (if any) for a single resource. Per-key
 * subscription means only THIS row re-renders when its effect changes. Safe to
 * call outside a provider (returns null) so leaf components don't hard-depend on
 * the provider being mounted.
 *
 * When an `instanceId` is given (a recurring calendar occurrence), the lookup
 * checks the occurrence-specific key FIRST, then falls back to the bare
 * whole-object key. So an INSTANCE effect (single-occurrence move/cancel) targets
 * exactly that occurrence, while a WHOLE-OBJECT effect (recurring/bulk create, a
 * whole-series update/delete — which the agent emits keyed by the master id with
 * NO instanceId) fans out to EVERY rendered occurrence of that object.
 */
export function useAgentEffect(
  resourceType: string | null | undefined,
  id: string | number | null | undefined,
  instanceId?: string,
): ActiveAgentEffect | null {
  const store = useContext(AgentEffectsCtx)
  const hasResource = Boolean(store && resourceType && id != null)
  const instanceKey = hasResource ? agentResourceKey(resourceType!, id!, instanceId) : null
  // Only when an instanceId narrows the key is a distinct bare-id fallback needed.
  const bareKey = hasResource && instanceId ? agentResourceKey(resourceType!, id!) : null

  const subscribe = useCallback(
    (listener: () => void) => {
      if (!store) return () => {}
      const unsubs: Array<() => void> = []
      if (instanceKey) unsubs.push(store.subscribe(instanceKey, listener))
      if (bareKey && bareKey !== instanceKey) unsubs.push(store.subscribe(bareKey, listener))
      return () => { for (const u of unsubs) u() }
    },
    [store, instanceKey, bareKey],
  )
  const getSnapshot = useCallback(() => {
    if (!store) return null
    if (instanceKey) {
      const inst = store.getEffect(instanceKey)
      if (inst) return inst
    }
    return bareKey ? store.getEffect(bareKey) : null
  }, [store, instanceKey, bareKey])

  return useSyncExternalStore(subscribe, getSnapshot, () => null)
}

/**
 * Normalize an event receipt's occurrence `instanceId` to the SAME token the
 * calendar chips key by (via `occToken`), so the receipt key equals the rendered
 * occurrence-chip key. The agent emits a raw `originalStartAt` (whatever
 * format/offset list_events returned); the chip derives its instanceId from its
 * own `originalStartAt` — normalizing both through `occInstanceToken` collapses
 * the format skew. Non-event effects and effects without an instanceId pass
 * through untouched; an unparseable token is DROPPED so the effect falls back to
 * the whole-object key rather than keying a bogus instance.
 */
function normalizeReceiptInstanceIds(receipt: AgentActionReceiptWireV1): AgentActionReceiptWireV1 {
  let changed = false
  const effects = receipt.effects.map((e): AgentActionEffectWireV1 => {
    const iid = e.resource.instanceId
    if (e.resource.type !== 'event' || !iid) return e
    const norm = occInstanceToken(iid)
    if (norm === iid) return e
    changed = true
    if (norm) return { ...e, resource: { ...e.resource, instanceId: norm } }
    const { instanceId: _drop, ...rest } = e.resource
    return { ...e, resource: rest }
  })
  return changed ? { ...receipt, effects } : receipt
}

/** Returns a stable function that ingests a validated wire receipt into the store. */
export function useIngestAgentReceipt(): (receipt: AgentActionReceiptWireV1) => void {
  const store = useContext(AgentEffectsCtx)
  return useCallback(
    (receipt: AgentActionReceiptWireV1) => {
      // Normalize occurrence instanceIds to the calendar's `occToken` form so a
      // single-occurrence move/cancel receipt keys the SAME slot the chip renders.
      store?.ingest(normalizeReceiptInstanceIds(receipt))
    },
    [store],
  )
}

/** Legacy bridge: feed a bare "agent created object id" (no receipt) with its type. */
export function useMarkAgentCreatedEffect(): (resourceType: string, id: string | number) => void {
  const store = useContext(AgentEffectsCtx)
  return useCallback(
    (resourceType: string, id: string | number) => {
      store?.markCreated(resourceType, id)
    },
    [store],
  )
}

/** Notify the store that a row's domain version is now `currentVersion` (stale-effect gating). */
export function useReconcileAgentEntity(): (key: string, currentVersion: number | undefined) => void {
  const store = useContext(AgentEffectsCtx)
  return useCallback(
    (key: string, currentVersion: number | undefined) => {
      store?.reconcileEntity(key, currentVersion)
    },
    [store],
  )
}

/**
 * Wire live entity versions to the coordinator so a concurrent NEWER entity
 * version (e.g. a human edit that landed after the agent's) suppresses a stale
 * agent highlight. Call once with the collection a view renders; items missing a
 * numeric `version` are skipped (a safe no-op). Cheap + idempotent — re-running
 * each render only clears effects whose `versionAfter` is now behind the row.
 */
export function useReconcileAgentEffects(
  resourceType: string,
  items: ReadonlyArray<{ id: string | number; instanceId?: string; version?: number }>,
): void {
  const reconcile = useReconcileAgentEntity()
  useEffect(() => {
    for (const it of items) {
      if (it.version == null) continue
      reconcile(agentResourceKey(resourceType, it.id, it.instanceId), it.version)
    }
  }, [resourceType, items, reconcile])
}
